import React, {Component, Fragment} from 'react';
import {connect} from "react-redux";
import { isFirefox } from 'react-device-detect';
import PropTypes from 'prop-types';

import Draggable from "react-draggable";
import log from '../../../util/log';
import colors from "../../../util/colors";
import config from "../../../util/config";
import Button from "../elements/Button";
import classnames from 'classnames';
import _ from 'lodash'
import {buildFontPicker, fontConstants, buildFontOption} from "../../../util/font-constants";
import PdfPreview from "./PdfPreview";
import {withVFTranslation} from "../../../util/withVFTranslation";
import SignatureRequest from "../../../models/SignatureRequest";
import utils from "../../../util/util";

class PdfSignatureImageOverlay extends Component {

  //Hack for the initial position
  OVERLAY_WIDTH = 358

  IMAGE_TYPES = {
    SIGNATURE : 'sig',
    INITIALS : 'init'
  }

  INITIAL_STATE = {
    signatureImageType : this.IMAGE_TYPES.SIGNATURE,
    controlledPosition : null,
    isDragging : false
  }

  constructor(props){
    super(props);

    this.wrapRef = React.createRef();
    this.imgRef = React.createRef();

    this.mouseDown = this.handleMouseDown.bind(this);

    this.state = this.INITIAL_STATE;
  }

  componentDidMount() {
    if(this.props.onRef) {
      this.props.onRef(this.props.id, this)
    }

    document.addEventListener('mousedown', this.mouseDown, false);

    this.onOverlayActivate();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if(!prevProps.isActive && this.props.isActive){
      this.onOverlayActivate();
    }
  }

  componentWillUnmount(){
    if(this.props.onRef) {
      this.props.onRef(this.props.id, undefined);
    }

    document.removeEventListener('mousedown', this.mouseDown, false);
  }

  initializePosition(signaturePositionList){

    let resCoords = {x : 0, y : 0};
    let scrollWindow = document.getElementById(PdfPreview.MODAL_SCROLL_CONTAINER);
    resCoords.y = scrollWindow.scrollTop + 200;
    resCoords.x = (scrollWindow.scrollLeft + (scrollWindow.offsetWidth / 2)) - (this.OVERLAY_WIDTH / 2);

    const findMatchingPosition = (coords) => _.find(signaturePositionList, (position) => coords.x === position.x && coords.y === position.y);

    const pxIncrement = 20;
    const positionSequence = [
      {dX : pxIncrement, dY : pxIncrement},
      {dX : -(pxIncrement * 2), dY : 0},
      {dX : 0, dY : -(pxIncrement * 2)},
      {dX : (pxIncrement * 2), dY : 0},
      {dX : 0, dY : 0}, //original position
    ]
    let tryCount = 0;
    let sequenceIndex = 0;
    let positionInLoop = resCoords;
    let newOverlayPosition = null;
    while(!newOverlayPosition){
      if(!positionSequence[sequenceIndex]){
        //If you've run through the sequence, bail and return the initial position.
        newOverlayPosition = resCoords;
      }
      else {
        let positionMatch = findMatchingPosition(positionInLoop);
        if (positionMatch) {
          let sequence = positionSequence[sequenceIndex];
          positionInLoop = {
            x: positionInLoop.x + sequence.dX,
            y: positionInLoop.y + sequence.dY
          }
          sequenceIndex++;
        }
        else {
          newOverlayPosition = positionInLoop;
        }

        tryCount++;
      }
    }

    log.log('picked overlay position', newOverlayPosition);

    this.setState({
      controlledPosition : newOverlayPosition
    })
  }

  getPickerOptions(){
    let { t } = this.props;

    return [
      {
        display: t("Signature"),
        val : this.IMAGE_TYPES.SIGNATURE
      },
      {
        display: t("Initials"),
        val : this.IMAGE_TYPES.INITIALS
      }
    ]
  }

  handleMouseDown(e){
    if(!this.wrapRef.current){
      return;
    }

    if(this.wrapRef.current.contains(e.target)){
      this.props.setActiveOverlay(this.props.id, true);
      return;
    }

    this.deactivateOverlay();
  }

  deactivateOverlay(){
    let { setActiveOverlay } = this.props;

    setActiveOverlay(this.props.id, false);
  }

  onOverlayActivate(){

  }

  handleDragStart(e, position){
    //log.log('drag start', e, position);

    //calculating drag bounds uses dom calls, and can be a little expensive on large docs.
    //lets just do it when drag starts and cache it.
    this.dragBounds = this.getDragBounds();

    const {x, y} = position;
    this.setState({
      isDragging : true,
      controlledPosition :{x, y}
    })
  }

  handleDrag(e, position){
    //log.log('handleDrag', e, position);

    const {x, y} = position;
    this.setState({
      controlledPosition :{x, y}
    })
  }

  handleDragStop(e, position){
    this.dragBounds = null;

    const {x, y} = position;
    this.setState({
      isDragging : false,
      controlledPosition :{x, y}
    })

    this.props.validateOverlay(this.props.id);
    // this.doFocusInput();
  }

  deleteSig(){
    this.setState(_.extend({}, this.INITIAL_STATE));
    this.props.deleteSignature(this.props.id);
  }

  getDragBounds(){
    let node = this.wrapRef.current;
    if(!node || !node.offsetParent){
      return 'parent';
    }

    //adjust the drag boundary according to each page to allow for dragging
    //beyond the right edge, which is what we need to place the left side of the overlay over there.
    let parent = node.offsetParent;
    let parentRect = parent.getBoundingClientRect();
    let pageRightEdge = 0;
    _.each(parent.querySelectorAll('.pdf-page-canvas'), (canvas) => {
      let rect = canvas.getBoundingClientRect();
      pageRightEdge = Math.max(pageRightEdge, rect.right - parentRect.left);
    })
    return {
      left : 0,
      top : 0,
      right : pageRightEdge > 0 ? pageRightEdge : parent.scrollWidth - node.offsetWidth,
      bottom : parent.scrollHeight - node.offsetHeight
    }
  }

  onSignatureTypeChange(val){
    this.setState({ signatureImageType : val })
  }

  getSignaturePositionOffsets(){
    let node = this.imgRef.current;
    let nodeStyle = window.getComputedStyle(node)

    let paddingLeft = PdfSignatureImageOverlay.parseValFromPx(nodeStyle.getPropertyValue('padding-left'))
    let paddingTop = PdfSignatureImageOverlay.parseValFromPx(nodeStyle.getPropertyValue('padding-top'))
    let imgHeight = PdfSignatureImageOverlay.parseValFromPx(nodeStyle.getPropertyValue('height'))
    let imgWidth = PdfSignatureImageOverlay.parseValFromPx(nodeStyle.getPropertyValue('width'))
    // let inputLineHeight = PdfSignatureImageOverlay.parseValFromPx(nodeStyle.getPropertyValue('line-height'))
    // var inputLineHeightOffset = (inputLineHeight - PdfSignatureImageOverlay.parseValFromPx(fontSize)) / 2;
    // let verticalOffset = 0;//PdfSigningOverlay.getVerticalFontOffset(font, fontSize);

    return {
      left : node.offsetLeft + paddingLeft,
      top : node.offsetTop + paddingTop,
      imgHeight,
      imgWidth
    }
  }

  setControlledPosition(x, y){
    this.setState({
      controlledPosition : {x, y}
    })
  }

  getControlledPosition(){
    return {
      x : this.state.controlledPosition.x,
      y : this.state.controlledPosition.y
    }
  }

  getImageForType(type){
    let { accountSignatures } = this.props;
    if(type === this.IMAGE_TYPES.SIGNATURE){
      return accountSignatures.sign_image;
    }
    else if(type === this.IMAGE_TYPES.INITIALS){
      return accountSignatures.init_image;
    }
  }

  getImageData(){
    return this.getImageForType(this.state.signatureImageType);
  }

  getOverlayValues(){
    let {
      controlledPosition
    } = this.state;

    let { id } = this.props;
    let offsets = this.getSignaturePositionOffsets();

    let signaturePosition = {
      x : controlledPosition.x + offsets.left,
      y : controlledPosition.y + offsets.top,
    }

    return {
      id,
      overlayPosition : controlledPosition,
      imgWidth : offsets.imgWidth,
      imgHeight : offsets.imgHeight,
      signaturePosition
    }
  }

  renderPicker(){
    let {
      signatureImageType
    } = this.state;

    return (
      <>
        {/*Pretty disappointed in react here.  In firefox we apparently can't get <select> values from the onChange handler here*/}
        {/*So our fix is to listen to click events on <option> elements, which then doesn't work anywhere else.  Pretty sloppy.*/}
        {isFirefox &&
          <Fragment>
            <select className="form-select me-2"
                    style={PdfSignatureImageOverlay.styles.fontPicker}
                    value={signatureImageType}
                    onChange={(evt) => evt.preventDefault()}>
              { this.getPickerOptions().map((option) => {
                //some problem between react and firefox makes us watch the onClick event on <option>
                //instead of watching onChange on the <select>.  that's crap guys.
                //I think it's this - https://github.com/facebook/react/issues/12584
                return <option key={option.val}
                               onClick={this.onSignatureTypeChange.bind(this, option.val)}
                               value={option.val}>
                  {option.display}
                </option>
              })}
            </select>
          </Fragment>
        }
        {!isFirefox &&
          <Fragment>
            <select className="form-select me-2"
                    style={PdfSignatureImageOverlay.styles.fontPicker}
                    value={signatureImageType}
                    onChange={(evt) => this.onSignatureTypeChange(evt.target.value)}>
              { this.getPickerOptions().map((option) => {
                //some problem between react and firefox makes us watch the onClick event on <option>
                //instead of watching onChange on the <select>.  that's crap guys.
                //I think it's this - https://github.com/facebook/react/issues/12584
                return <option key={option.val}
                               value={option.val}>
                  {option.display}
                </option>
              })}
            </select>
          </Fragment>
        }
      </>
    )
  }

  render(){
    let {
      isDragging,
      signatureImageType,
      controlledPosition
    } = this.state;
    let {
      isActive,
      isValidPosition,
      accountSignatures,
    } = this.props;

    let zIndex = 10;
    let indicatorColor = colors.SIGNING;
    let overlayColor = colors.SIGNING;
    let headerStyle = PdfSignatureImageOverlay.styles.header;
    let imageData = this.getImageData();
    if(!isActive){
      zIndex = 1;
      overlayColor = colors.TRANSPARENT;
      headerStyle = {...headerStyle, ...PdfSignatureImageOverlay.styles.invisible}
    }

    if(isDragging){
      overlayColor = colors.SIGNING_HOVER;
      indicatorColor = colors.SIGNING_HOVER;
    }
    else if(!isValidPosition){
      overlayColor = colors.SIGNING_INVALID;
      indicatorColor = colors.SIGNING_INVALID;
    }

    if(!controlledPosition){
      return ( < Fragment /> )
    }

    return (
      <Draggable
        cancel={'select, button, .not-draggable'}
        bounds={this.dragBounds || 'parent'}
        scale={1}
        position={controlledPosition}
        onStart={this.handleDragStart.bind(this)}
        onDrag={this.handleDrag.bind(this)}
        onStop={this.handleDragStop.bind(this)}>
        <div ref={this.wrapRef} style={{...PdfSignatureImageOverlay.styles.overlay, zIndex}}>
          <div>
            <div style={{...headerStyle, backgroundColor : overlayColor}}>
              {this.renderPicker()}
              <Button className="btn signing-v1-delete-signature" onClick={this.deleteSig.bind(this)}>
                <i className="icon ion-trash-a" />
              </Button>
            </div>
            <div style={PdfSignatureImageOverlay.styles.bottomRow}>
              <i style={{...PdfSignatureImageOverlay.styles.iconIndicator, color : indicatorColor}}
                 className="icon ion-arrow-right-b" />
              <div>
                <img src={imageData}
                     ref={this.imgRef}
                     style={{
                       verticalAlign : 'super',
                       height: '48px'
                     }}/>
              </div>
            </div>
          </div>
        </div>
      </Draggable>
    )
  }
}

PdfSignatureImageOverlay.parseValFromPx = (pxVal) => {
  return +pxVal.split('px')[0];
}

PdfSignatureImageOverlay.styles = {
  overlay : {
    position: 'absolute',
  },
  invisible : {
    userSelect : 'none',
    pointerEvents : 'none',
    opacity : 0,
    pointer : 'default'
  },
  header : {
    color : colors.LIGHT,
    paddingLeft: '40px',
    paddingRight: '40px',
    borderTopLeftRadius: '40px',
    borderTopRightRadius: '40px',
    display : 'flex',
    paddingTop : '5px',
    paddingBottom : '5px',
    cursor : 'pointer'
  },
  bottomRow : {
    display : 'flex',
    cursor : 'pointer'
  },
  iconIndicator: {
    fontSize: '30px',
    lineHeight : '38px',
  },
  fontPicker : {
    minWidth: '170px'
  },
  fontSizePicker :{
    minWidth: '55px'
  },
  input : {
    padding : '0px 5px',
    color : colors.BLACK,
    verticalAlign: 'baseline',
    lineHeight : '38px',
    boxShadow : 'none',
    border : 'none',
    backgroundColor: colors.TRANSPARENT,
  }
}

const mapStateToProps = (state) => {
  return {
    accountSignatures : state.shared.signatures
  }
}

const mapDispatchToProps = (dispatch) => {
  return {};
};

PdfSignatureImageOverlay.propTypes = {
  id : PropTypes.string.isRequired,
  isActive : PropTypes.bool.isRequired,
  isValidPosition : PropTypes.bool.isRequired,
  validateOverlay : PropTypes.func.isRequired,
  setActiveOverlay : PropTypes.func.isRequired,
  deleteSignature : PropTypes.func.isRequired,
  onRef : PropTypes.func.isRequired
}

export default withVFTranslation()(connect(mapStateToProps, mapDispatchToProps)(PdfSignatureImageOverlay));
